import 'react-perfect-scrollbar/dist/css/styles.css'
import 'react-datepicker/dist/react-datepicker.css'

import { StyledEngineProvider, ThemeProvider } from '@material-ui/core'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useRoutes } from 'react-router-dom'
import theme from 'src/assets/theme'
import ErrorNotification from 'src/components/helperComponents/ErrorNotification'
import { userSignOut } from 'src/redux/actions'
import routes from 'src/routes'
import API from 'src/util/api'

import GlobalStyles from './components/GlobalStyles'
import useToken from './hooks/useToken'
import localStorageService from './util/localStorageService'

const queryClient = new QueryClient();

const App = () => {
  const { token, setToken } = useToken();
  const content = useRoutes(routes(token));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isLoggedInRef = useRef(false)
  isLoggedInRef.current = !!token

  const verifyUser = useCallback(() => {
    API.post('refreshToken')
      .then(data => {
        localStorageService.setAuthInfo(data)
        // The context token is only a logged-in flag (requests read the real
        // token from localStorage). Re-setting it on every 5-minute renewal
        // re-rendered the whole app, which made pages look like they refresh
        // on a timer — so only set it when the login state actually changes.
        if (!isLoggedInRef.current) {
          setToken(data.token)
        }
        // call refreshToken every 5 minutes to renew the authentication token.
        setTimeout(verifyUser, 60 * 1000 * 5)
      })
      .catch(() => {
        // session-expiry cleanup/redirect is handled by the 'auth:expired' listener below
      })
  }, [])

  useEffect(() => {
    verifyUser()
  }, [verifyUser])

  useEffect(() => {
    const handleSessionExpired = () => {
      dispatch(userSignOut())
      setToken(null)
      navigate('/login', { replace: true })
    }

    window.addEventListener('auth:expired', handleSessionExpired)
    return () => window.removeEventListener('auth:expired', handleSessionExpired)
  }, [dispatch, navigate, setToken])

  return (
    <QueryClientProvider client={queryClient}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <GlobalStyles />
          {content}
          <ErrorNotification />
        </ThemeProvider>
      </StyledEngineProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
